import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { ORG_ADMIN_SCOPE, ORG_CONTACT_DATA_SCOPE, ORG_READ_SCOPE } from "./auth/orgRoutes";
import { userCanManageOrgApps, userHasGlobalScope, userHasOrgScope } from "./auth/scopes";
import { FEATURE_MICRO_APPS, useOrgFeature } from "./auth/features.ts";
import { AuthProvider, useAuth } from "./contexts/AuthContext.tsx";
import { OrganizationProvider, useOrganization } from "./contexts/OrganizationContext.tsx";
import { TenantProvider, useTenant } from "./contexts/TenantContext.tsx";
import { useSseInvalidations } from "./realtime/useSseInvalidations.ts";
import AppLayout from "./layouts/AppLayout.tsx";
import AcceptInvitationPage from "./pages/AcceptInvitationPage.tsx";
import CompaniesPage from "./pages/companies/CompaniesPage.tsx";
import CompanyDetailPage from "./pages/company-detail/CompanyDetailPage.tsx";
import OrganizationDashboardPage from "./pages/OrganizationDashboardPage.tsx";
import PlatformLayout from "./pages/platform/PlatformLayout.tsx";
import PlatformOverviewPage from "./pages/platform/PlatformOverviewPage.tsx";
import PlatformRecurringPage from "./pages/platform/PlatformRecurringPage.tsx";
import PlatformSignalsPage from "./pages/platform/PlatformSignalsPage.tsx";
import ContactsPage from "./pages/contacts/ContactsPage.tsx";
import ContactDetailPage from "./pages/contacts/ContactDetailPage.tsx";
import AtsFeedsPage from "./pages/AtsFeedsPage.tsx";
import JobAdDetailPage from "./pages/JobAdDetailPage.tsx";
import JobAdsPage from "./pages/JobAdsPage.tsx";
import LoginPage from "./pages/LoginPage.tsx";
import SsoMfaPage from "./pages/SsoMfaPage.tsx";
import McpGuidePage from "./pages/McpGuidePage.tsx";
import AccountLayout from "./pages/account/AccountLayout.tsx";
import ConnectedAppsPage from "./pages/account/ConnectedAppsPage.tsx";
import ProfilePage from "./pages/account/ProfilePage.tsx";
import SecurityPage from "./pages/account/SecurityPage.tsx";
import MergeSuggestionsPage from "./pages/MergeSuggestionsPage.tsx";
import NewsArticleDetailPage from "./pages/NewsArticleDetailPage.tsx";
import NewsArticlesPage from "./pages/NewsArticlesPage.tsx";
import RegulatoryFilingDetailPage from "./pages/RegulatoryFilingDetailPage.tsx";
import RegulatoryFilingsPage from "./pages/RegulatoryFilingsPage.tsx";
import TenderDetailPage from "./pages/TenderDetailPage.tsx";
import TendersPage from "./pages/TendersPage.tsx";
import ProspectListsPage from "./pages/ProspectListsPage.tsx";
import ProspectListDetailPage from "./pages/prospect-list-detail/ProspectListDetailPage.tsx";
import SettingsPage from "./pages/SettingsPage.tsx";
import SkillsPage from "./pages/settings/global/SkillsPage.tsx";
import MicroAppsPage from "./pages/settings/global/MicroAppsPage.tsx";
import MicroAppsIndexPage from "./pages/micro-apps/MicroAppsIndexPage.tsx";
import MicroAppHostPage from "./pages/micro-apps/MicroAppHostPage.tsx";
import TvApp from "./pages/tv/TvApp.tsx";
import WorkflowsPage from "./pages/workflows/WorkflowsPage.tsx";
import WorkflowEditorPage from "./pages/workflows/editor/WorkflowEditorPage.tsx";
import WorkflowsLayout from "./pages/workflows/WorkflowsLayout.tsx";
import SignalLogPage from "./pages/workflows/signals/SignalLogPage.tsx";
import SignalPerformancePage from "./pages/workflows/signals/SignalPerformancePage.tsx";
import ProspectsPage from "./pages/prospects/ProspectsPage.tsx";
import ProspectsOverviewPage from "./pages/prospects/ProspectsOverviewPage.tsx";
import SystemPromptsPage from "./pages/settings/global/SystemPromptsPage.tsx";
import AgentDefinitionsPage from "./pages/settings/global/AgentDefinitionsPage.tsx";
import ImportsPage from "./pages/settings/global/ImportsPage.tsx";
import BackgroundJobsPage from "./pages/settings/global/BackgroundJobsPage.tsx";
import DatabasePage from "./pages/settings/global/DatabasePage.tsx";
import UiKitPage from "./pages/settings/global/UiKitPage.tsx";
import AiGenerationLogsPage from "./pages/settings/global/AiGenerationLogsPage.tsx";
import RolesPermissionsPage from "./pages/settings/global/RolesPermissionsPage.tsx";
import TrainingDataExportsPage from "./pages/settings/global/TrainingDataExportsPage.tsx";
import InferenceInstancesPage from "./pages/settings/global/InferenceInstancesPage.tsx";
import DataProvidersPage from "./pages/settings/global/DataProvidersPage.tsx";
import NewsFeedsPage from "./pages/settings/global/NewsFeedsPage.tsx";
import UsersPage from "./pages/settings/global/UsersPage.tsx";
import OrganizationsPage from "./pages/settings/global/OrganizationsPage.tsx";
import OrganizationDetailPage from "./pages/settings/global/OrganizationDetailPage.tsx";
import ContactDataPage from "./pages/organization/ContactDataPage.tsx";
import OrgDetailsPage from "./pages/organization/DetailsPage.tsx";
import MembersPage from "./pages/organization/MembersPage.tsx";
import OrgTenantsPage from "./pages/organization/TenantsPage.tsx";
import OrdersPage from "./pages/organization/orders/OrdersPage.tsx";
import OrderDetailPage from "./pages/organization/orders/OrderDetailPage.tsx";
import IntegrationsPage from "./pages/organization/integrations/IntegrationsPage.tsx";

function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function SseInvalidator() {
  useSseInvalidations();
  return null;
}

function RequireGlobalScope({ scope }: { scope: string }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!userHasGlobalScope(user, scope)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Guard for org-scoped routes. By default uses the currently-selected org from
 * OrganizationContext; pass `orgId` to override (e.g. for routes with an :id param).
 */
function RequireOrgScope({ scope, orgId }: { scope: string; orgId?: string }) {
  const { user, isLoading } = useAuth();
  const { selectedOrgId } = useOrganization();
  if (isLoading) return null;
  const targetOrgId = orgId ?? selectedOrgId;
  if (!userHasOrgScope(user, targetOrgId, scope)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Guard for micro-app management. Admits a publisher/developer (global
 * `apps:write`) or an admin of the currently-selected org, mirroring the backend
 * `assert_can_manage_org_apps`.
 */
function RequireManageApps() {
  const { user, isLoading } = useAuth();
  const { selectedOrgId } = useOrganization();
  if (isLoading) return null;
  if (!userCanManageOrgApps(user, selectedOrgId)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Guard for routes of an organization feature flag the selected org may lack. */
function RequireFeature({ feature }: { feature: string }) {
  const { isLoading } = useAuth();
  const enabled = useOrgFeature(feature);
  if (isLoading) return null;
  if (!enabled) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Resolves the default tenant (remembered, else first accessible) and redirects
 * to its tenant-scoped root `/t/<slug>`. Falls back to the dashboard's own
 * empty state when the user has no tenants.
 */
function DefaultTenantRedirect() {
  const { tenants, selectedTenant, isLoading } = useTenant();
  if (isLoading) return null;
  const tenant = selectedTenant ?? tenants[0];
  if (!tenant) return <OrganizationDashboardPage />;
  return <Navigate to={`/t/${tenant.slug}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <SseInvalidator />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso/mfa" element={<SsoMfaPage />} />
        <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
        {/* TV-shared micro apps, for local development only: production serves
            them on the dash host, and hive.sxq.no does not route /api/v1/tv. */}
        {import.meta.env.DEV ? <Route path="/tv/*" element={<TvApp />} /> : null}
        <Route element={<RequireAuth />}>
          <Route
            element={
              <OrganizationProvider>
                <TenantProvider>
                  <AppLayout />
                </TenantProvider>
              </OrganizationProvider>
            }
          >
            {/* Tenant-scoped pages live under /t/:tenantSlug */}
            <Route path="/t/:tenantSlug">
              <Route index element={<OrganizationDashboardPage />} />
              <Route path="companies" element={<CompaniesPage />} />
              <Route path="companies/:id" element={<CompanyDetailPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="contacts/:id" element={<ContactDetailPage />} />
              <Route path="prospects">
                <Route index element={<ProspectsOverviewPage />} />
                <Route path="inbox" element={<ProspectsPage queue="inbox" key="inbox" />} />
                <Route path="worklist" element={<ProspectsPage queue="worklist" key="worklist" />} />
                <Route path="done" element={<ProspectsPage queue="done" key="done" />} />
                <Route path="collections" element={<ProspectListsPage />} />
                <Route path="collections/:id" element={<ProspectListDetailPage />} />
              </Route>
              <Route element={<RequireGlobalScope scope="signal_rules:read" />}>
                <Route path="workflows" element={<WorkflowsLayout />}>
                  <Route index element={<WorkflowsPage />} />
                  <Route element={<RequireGlobalScope scope="signals:read" />}>
                    <Route path="signals" element={<SignalLogPage />} />
                    <Route path="signals/performance" element={<SignalPerformancePage />} />
                  </Route>
                </Route>
                <Route path="workflows/:id" element={<WorkflowEditorPage />} />
              </Route>
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* The root opens the active tenant. */}
            <Route path="/" element={<DefaultTenantRedirect />} />

            <Route element={<RequireGlobalScope scope="platform:read" />}>
              <Route path="/platform" element={<PlatformLayout />}>
                <Route index element={<PlatformOverviewPage />} />
                <Route path="signals" element={<PlatformSignalsPage />} />
                <Route path="recurring" element={<PlatformRecurringPage />} />
              </Route>
            </Route>
            <Route path="/job-ads" element={<JobAdsPage />} />
            <Route path="/job-ads/:id" element={<JobAdDetailPage />} />
            <Route path="/regulatory-filings" element={<RegulatoryFilingsPage />} />
            <Route path="/regulatory-filings/:id" element={<RegulatoryFilingDetailPage />} />
            <Route path="/tenders" element={<TendersPage />} />
            <Route path="/tenders/:id" element={<TenderDetailPage />} />
            <Route path="/news-articles" element={<NewsArticlesPage />} />
            <Route path="/news-articles/:id" element={<NewsArticleDetailPage />} />
            <Route path="/ats-feeds" element={<AtsFeedsPage />} />
            <Route element={<RequireGlobalScope scope="merge-suggestions:manage" />}>
              <Route path="/merge-review" element={<MergeSuggestionsPage />} />
            </Route>
            <Route path="/connect/mcp" element={<McpGuidePage />} />
            <Route path="/account" element={<AccountLayout />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="apps" element={<ConnectedAppsPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="imports:read" />}>
              <Route path="/settings/imports" element={<ImportsPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="users:manage" />}>
              <Route path="/settings/users" element={<UsersPage />} />
              <Route path="/settings/organizations" element={<OrganizationsPage />} />
              <Route path="/settings/organizations/:id" element={<OrganizationDetailPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="system-prompts:read" />}>
              <Route path="/settings/system-prompts" element={<SystemPromptsPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="agent-definitions:write" />}>
              <Route path="/settings/agents" element={<AgentDefinitionsPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="platform:read" />}>
              <Route path="/settings/skills" element={<SkillsPage />} />
            </Route>
            <Route element={<RequireFeature feature={FEATURE_MICRO_APPS} />}>
              <Route element={<RequireGlobalScope scope="apps:read" />}>
                <Route path="/apps" element={<MicroAppsIndexPage />} />
                <Route path="/apps/:slug" element={<MicroAppHostPage />} />
              </Route>
              <Route element={<RequireManageApps />}>
                <Route path="/settings/micro-apps" element={<MicroAppsPage />} />
              </Route>
            </Route>
            <Route element={<RequireGlobalScope scope="background-jobs:read" />}>
              <Route path="/settings/background-jobs" element={<BackgroundJobsPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="training-data:read" />}>
              <Route
                path="/settings/training-data-exports"
                element={<TrainingDataExportsPage />}
              />
            </Route>
            <Route element={<RequireGlobalScope scope="dev:read" />}>
              <Route path="/settings/database" element={<DatabasePage />} />
              <Route path="/settings/ai-generation-logs" element={<AiGenerationLogsPage />} />
              <Route path="/settings/roles-permissions" element={<RolesPermissionsPage />} />
              <Route path="/settings/ui-kit" element={<UiKitPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="inference-instances:manage" />}>
              <Route
                path="/settings/inference-instances"
                element={<InferenceInstancesPage />}
              />
            </Route>
            <Route element={<RequireGlobalScope scope="data-providers:manage" />}>
              <Route path="/settings/data-providers" element={<DataProvidersPage />} />
            </Route>
            <Route element={<RequireGlobalScope scope="news-feeds:manage" />}>
              <Route path="/settings/news-feeds" element={<NewsFeedsPage />} />
            </Route>
            <Route element={<RequireOrgScope scope={ORG_ADMIN_SCOPE} />}>
              <Route path="/organization/details" element={<OrgDetailsPage />} />
              <Route path="/organization/members" element={<MembersPage />} />
              <Route path="/organization/tenants" element={<OrgTenantsPage />} />
              <Route path="/organization/integrations" element={<IntegrationsPage />} />
            </Route>
            <Route element={<RequireOrgScope scope={ORG_CONTACT_DATA_SCOPE} />}>
              <Route path="/organization/contact-data" element={<ContactDataPage />} />
            </Route>
            <Route element={<RequireOrgScope scope={ORG_READ_SCOPE} />}>
              <Route path="/organization/orders" element={<OrdersPage />} />
              <Route path="/organization/orders/:id" element={<OrderDetailPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}

export default App;
